/* @flow */
import gql from 'graphql-tag';
import {isToday, parseISO} from 'date-fns';
import {evaluationIsVariant} from '@homeaway/react-experiment';
import {fragments as destinationBreadcrumbsFragments} from '../components/Breadcrumb/Breadcrumb';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {fragments as mapFragments} from '../components/Map/Map';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as destinationMessage} from '../components/SearchBannerContainer/destinationMessage';
import {fragments as filterCountsFragments} from '../../universal/queries/filteredCountQuery';
import {fragments as expandedGroupsFragments} from '../components/ExpandedGroups/ExpandedGroups';
import {fragments as analyticsDataLayerFragments} from '../components/AnalyticsDataLayer/AnalyticsDataLayer';
import {fragments as searchTermCarouselFragments} from '../components/SearchTermCarousel/SearchTermCarousel';
import {fragments as seoContentFragment} from '../components/SeoContent/SeoContent';
import {fragments as internalToolsFragments} from '../components/InternalTools/InternalTools';
import {fragments as seoMetaDataParamsFragments} from '../components/withSeoMetaData/withSeoMetaData';
import transformSrsParamsToGts from '../../server/graphql/transformers/transformSrsParamsToGts';
import {querySelectionSet} from './searchQuery';
import {VRBO_WEB_SERP_FEWER_INITIAL_RESULTS} from '../../universal/abacus/experiments';

import type {State as ReduxState} from '../reducers/index';
import type {SearchParamsType} from '../reducers/searchParams';

type AbacusState = $PropertyType<ReduxState, 'abacus'>;
type SystemState = $PropertyType<ReduxState, 'system'>;

export const searchRequestQueryVariablesOverrides = (
    params: SearchParamsType,
    abacus: AbacusState,
    system: SystemState,
    isFilteredCountQuery: boolean = false
): SearchParamsType => {
    const {badBotPageSize, isBadBot} = system;
    let newParams: SearchParamsType = params;
    const {arrival} = newParams;

    // Handle variable page sizing
    if (!isFilteredCountQuery) {
        if (isBadBot) {
            newParams = {
                ...params,
                // defined in manifest.json
                pageSize: badBotPageSize
            };
        }

        if (
            !isBadBot &&
            evaluationIsVariant(abacus[VRBO_WEB_SERP_FEWER_INITIAL_RESULTS], 1)
        ) {
            newParams = {
                ...params,
                pageSize: 25
            };
        }
    }

    // Why is this business logic buried here? We need to find a better place for it.
    if (arrival && isToday(parseISO(arrival))) {
        newParams = {
            ...params,
            isSameDaySelected: true
        };
    }

    return newParams;
};

export const searchRequestQueryVariables = (
    params: SearchParamsType,
    abacus: AbacusState,
    system: SystemState,
    // TODO: we need to use the same variables for both searching and fetching
    // filter counts, but this is a clumsy way to tell the override function not
    // to mess with filter count pageSize, which needs to be set low so that
    // filter counts can be fetched cheaply.
    isFilteredCountQuery: boolean = false
) => {
    const newParams = searchRequestQueryVariablesOverrides(
        params,
        abacus,
        system,
        isFilteredCountQuery
    );

    return {
        filterCounts: system.searchFilterCounts,
        request: transformSrsParamsToGts(newParams),
        gtsQuery: true
    };
};

export const SEARCH_REQUEST_QUERY_NAME = 'SearchRequestQuery';

const searchRequestQuery = gql`
    query ${SEARCH_REQUEST_QUERY_NAME}($request: SearchResultRequest!, $gtsQuery: Boolean!, $filterCounts: Boolean!) {
        results: search(request: $request) {
            ${querySelectionSet}
            ...DestinationBreadcrumbsSearchResult
            ...DestinationMessageSearchResult
            ...FilterCountsSearchResult
            ...HitCollectionSearchResult
            ...ADLSearchResult
            ...MapSearchResult
            ...ExpandedGroupsSearchResult
            ...PagerSearchResult
            ...SearchTermCarouselSearchResult
            ...SeoContentSearchResult
            ...InternalToolsSearchResult
            ...SEOMetaDataParamsSearchResult
        }
        ...RequestMarkerFragment
    }
    ${destinationBreadcrumbsFragments.searchResult}
    ${hitCollectionFragments.searchResult}
    ${expandedGroupsFragments.searchResult}
    ${filterCountsFragments.searchResult}
    ${mapFragments.searchResult}
    ${pagerFragments.searchResult}
    ${destinationMessage.searchResult}
    ${analyticsDataLayerFragments.searchResult}
    ${analyticsDataLayerFragments.query}
    ${searchTermCarouselFragments.searchResult}
    ${seoContentFragment.searchResult}
    ${internalToolsFragments.searchResult}
    ${seoMetaDataParamsFragments.searchResult}
`;

export default searchRequestQuery;
